import React from "react";
import { C, themeColors } from "../lib/colors.js";
import { toggleStroke } from "../lib/iconStroke.js";

// The Bug / Settings / Account icons, shared by the Signal Library bar (IndicesBar) and the
// navigation rail so both draw the same icon, the same hover, and the same open state.
// Extra props (data attributes, a click handler, keyboard focus) pass through to the box.

const boxStyle = (lit) => ({
  cursor: "pointer",
  userSelect: "none",
  opacity: lit ? 1 : 0.6,
  transition: "opacity 0.15s",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  lineHeight: 0,
  flexShrink: 0,
});

const hoverHandlers = (lit) => ({
  onMouseEnter: (e) => {
    if (!lit) e.currentTarget.style.opacity = 1;
  },
  onMouseLeave: (e) => {
    if (!lit) e.currentTarget.style.opacity = 0.6;
  },
});

/** The bug icon; `flash` is the post-tour attention pulse. */
export function BugIcon({ theme, flash, style, ...rest }) {
  return (
    <div
      {...hoverHandlers(flash)}
      {...rest}
      style={{
        ...boxStyle(flash),
        ...(flash
          ? {
              animation: "tourFlash 0.9s steps(1,end) infinite",
              filter: `drop-shadow(0 0 6px ${C.green})`,
            }
          : {}),
        ...style,
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        // raw-hex stroke (not the var-based `C`): an SVG presentation attribute
        // doesn't resolve var(). Attribute (not style) so it inherits to the
        // child paths and doesn't collide with the `style` below.
        stroke={
          flash
            ? themeColors(theme).green
            : theme === "dusk"
              ? themeColors(theme).accent
              : theme === "dev"
                ? themeColors(theme).action
                : themeColors(theme).grayL
        }
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block" }}
      >
        <g transform="translate(0,1.5)">
          <path d="M9 4a3 3 0 0 1 6 0" />
          <rect x="7" y="7" width="10" height="13" rx="5" />
          <line x1="3" y1="11" x2="7" y2="11" />
          <line x1="17" y1="11" x2="21" y2="11" />
          <line x1="3" y1="17" x2="7" y2="17" />
          <line x1="17" y1="17" x2="21" y2="17" />
          <line x1="12" y1="7" x2="12" y2="20" />
        </g>
      </svg>
    </div>
  );
}

/** The settings gear. */
export function SettingsIcon({ theme, open, style, ...rest }) {
  return (
    <div {...hoverHandlers(open)} {...rest} style={{ ...boxStyle(open), ...style }}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={toggleStroke(theme, open)}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block" }}
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </div>
  );
}

/** The account (person) icon. */
export function AccountIcon({ theme, open, style, ...rest }) {
  return (
    <div {...hoverHandlers(open)} {...rest} style={{ ...boxStyle(open), ...style }}>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke={toggleStroke(theme, open)}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ display: "block" }}
      >
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    </div>
  );
}
